/**
 * @file ContractDrawerEditModeBannerView.tsx
 * @description Banner shown above the inline editor while a previously
 *   SAVED extraction is being edited (SE-4347 AC-3). Carries the
 *   "changes will move status to Pending Review" disclosure plus a
 *   Cancel button wired to the edit-mode hook's `requestCancel`. SE-4768
 *   adds a `saveAction` slot rendered alongside Cancel so users see
 *   Save and Cancel as a paired transactional affordance during edit
 *   mode (SE-4767 only inlined Save into the StatusStrip, which is
 *   hidden while editing).
 * @module features/contracts/components/ContractDrawerEditModeBanner/ContractDrawerEditModeBannerView
 */

import { Pencil } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";

/** Props for {@link ContractDrawerEditModeBannerView}. */
interface Props {
  /** Whether the banner should be visible. */
  readonly isVisible: boolean;
  /** Invoked when the user taps Cancel — wired to `requestCancel`. */
  readonly onCancelPress: () => void;
  /**
   * Optional slot rendered to the LEFT of the Cancel button (SE-4768).
   * Used by the drawer body to inline a Save affordance when
   * `canSave === true` so the user sees Save + Cancel as a paired
   * transactional set during edit mode. Null when the extraction has
   * not yet flipped to PENDING_REVIEW (no edits made yet) so the
   * banner stays Cancel-only.
   */
  readonly saveAction?: React.ReactNode;
}

/**
 * Banner that flags the user is currently editing a SAVED extraction. Cancel (SE-4768) server-side status transition happens inside the existing line-item mutations (`addLineItem` / `editLineItem` / `deleteLineItem` from SE-4343) — there is no separate "enter edit mode" round-trip. Save commits via `saveContractExtraction`; Cancel + Discard reverts via `revertContractExtraction` (SE-4771) — both routed through the parent container.
 * @param props - Component props
 * @param props.isVisible - Whether the banner should be visible.
 * @param props.onCancelPress - Invoked when the user taps Cancel — wired to `requestCancel`.
 * @param props.saveAction - Optional slot rendered to the LEFT of the Cancel button (SE-4768). Used by the drawer body to inline a Save affordance when `canSave === true` so the user sees Save + Cancel as a paired transactional set during edit mode. Null when the extraction has not yet flipped to PENDING_REVIEW (no edits made yet) so the banner stays Cancel-only.
 * @returns The rendered banner, or null when hidden
 */
const ContractDrawerEditModeBannerView = ({
  isVisible,
  onCancelPress,
  saveAction,
}: Props) =>
  !isVisible ? null : (
    <Box testID="contract-drawer-edit-mode-banner">
      <Box className="rounded-none border border-needs-semantic-warning-300 bg-needs-semantic-warning-50 p-3">
        <Stack
          direction="horizontal"
          className="items-center justify-between gap-3"
        >
          <Stack direction="horizontal" className="flex-1 items-center gap-2">
            <Icon
              as={Pencil}
              size="sm"
              className="text-status-warning-strong"
            />
            <Stack className="flex-1">
              <Text
                variant="body-strong"
                className="text-needs-semantic-warning-700"
              >
                Editing
              </Text>
              <Text
                variant="caption"
                className="text-needs-semantic-warning-700"
              >
                Changes will move status to Pending Review.
              </Text>
            </Stack>
          </Stack>
          <Stack direction="horizontal" className="items-center gap-2">
            {saveAction}
            <Button
              testID="contract-drawer-edit-cancel-btn"
              size="sm"
              variant="outline"
              tone="secondary"
              onPress={onCancelPress}
              label="Cancel"
            />
          </Stack>
        </Stack>
      </Box>
    </Box>
  );

ContractDrawerEditModeBannerView.displayName =
  "ContractDrawerEditModeBannerView";

export default memo(ContractDrawerEditModeBannerView);
